import React from 'react';
import {
  DialogActions,
  DialogActionsProps,
  Button,
  ButtonProps,
  IconButton,
  Tooltip,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';

export interface ResponsiveDialogActionButton {
  /**
   * Texto do botão
   */
  label: string;
  /**
   * Texto curto para mobile (opcional)
   * Se não fornecido e houver ícone, em mobile mostra apenas o ícone
   */
  mobileLabel?: string;
  /**
   * Ícone do botão (opcional)
   * Em mobile, se houver ícone e não houver mobileLabel, mostra apenas o ícone
   */
  icon?: React.ReactNode;
  /**
   * Tooltip para quando mostrar apenas ícone em mobile
   */
  tooltipTitle?: string;
  /**
   * Props do botão do Material-UI
   */
  buttonProps?: ButtonProps;
  /**
   * Handler do clique
   */
  onClick: () => void;
}

export interface ResponsiveDialogActionsProps extends Omit<DialogActionsProps, 'children'> {
  /**
   * Botões primários (ação principal, geralmente à direita)
   */
  primaryActions?: ResponsiveDialogActionButton[];
  /**
   * Botões secundários (ações de cancelar, geralmente à esquerda)
   */
  secondaryActions?: ResponsiveDialogActionButton[];
  /**
   * Breakpoint para considerar como mobile
   * @default 'sm'
   */
  mobileBreakpoint?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

/**
 * Componente DialogActions responsivo que padroniza botões em diálogos.
 * 
 * Em mobile:
 * - Botões ficam em coluna (fullWidth)
 * - Botão primário aparece primeiro (ordem visual)
 * - Textos curtos são usados se mobileLabel fornecido
 * 
 * Em desktop:
 * - Botões ficam em linha
 * - Botões secundários à esquerda, primários à direita
 * - Textos completos são usados
 * 
 * Segue as melhores práticas:
 * - Tamanho mínimo de toque de 44x44px
 * - Ordem visual correta (primário primeiro em mobile)
 * - Textos adaptativos
 */
const ResponsiveDialogActions: React.FC<ResponsiveDialogActionsProps> = ({
  primaryActions = [],
  secondaryActions = [],
  mobileBreakpoint = 'sm',
  sx,
  ...dialogActionsProps
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(mobileBreakpoint));

  const renderButton = (action: ResponsiveDialogActionButton, _isPrimary: boolean) => {
    // Em mobile: se houver ícone e não houver mobileLabel, mostrar apenas ícone
    const showIconOnly = isMobile && action.icon && !action.mobileLabel;
    const displayLabel = isMobile && action.mobileLabel 
      ? action.mobileLabel 
      : action.label;

    // Se for mostrar apenas ícone em mobile
    if (showIconOnly) {
      const iconButton = (
        <IconButton
          key={action.label}
          {...(action.buttonProps as Record<string, unknown>)}
          onClick={action.onClick}
          sx={{
            minWidth: 48,
            minHeight: 48,
            padding: '12px',
            ...action.buttonProps?.sx,
          }}
        >
          {action.icon}
        </IconButton>
      );

      // Se houver tooltip, envolver o IconButton
      if (action.tooltipTitle) {
        return (
          <Tooltip key={action.label} title={action.tooltipTitle} arrow>
            {iconButton}
          </Tooltip>
        );
      }

      return iconButton;
    }

    // Caso contrário, mostrar botão completo
    return (
      <Button
        key={action.label}
        {...action.buttonProps}
        onClick={action.onClick}
        startIcon={action.icon}
        fullWidth={isMobile}
        sx={{
          minWidth: isMobile ? 'auto' : 120,
          minHeight: isMobile ? 44 : undefined,
          fontSize: isMobile ? '0.875rem' : undefined,
          padding: isMobile ? '10px 16px' : undefined,
          ...action.buttonProps?.sx,
        }}
      >
        {displayLabel}
      </Button>
    );
  };

  // Separar botões com ícone apenas dos botões com texto em mobile
  const getIconOnlyActions = (actions: ResponsiveDialogActionButton[]) => {
    return actions.filter(action => isMobile && action.icon && !action.mobileLabel);
  };

  const getTextActions = (actions: ResponsiveDialogActionButton[]) => {
    return actions.filter(action => !(isMobile && action.icon && !action.mobileLabel));
  };

  // Em mobile, separar botões com ícone apenas dos botões com texto
  const primaryIconOnly = isMobile ? getIconOnlyActions(primaryActions) : [];
  const primaryText = isMobile ? getTextActions(primaryActions) : primaryActions;
  const secondaryIconOnly = isMobile ? getIconOnlyActions(secondaryActions) : [];
  const secondaryText = isMobile ? getTextActions(secondaryActions) : secondaryActions;

  return (
    <DialogActions
      {...dialogActionsProps}
      sx={{
        px: 3,
        pb: 3,
        gap: isMobile ? 2 : 1,
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'flex-end',
        alignItems: isMobile ? 'stretch' : 'center',
        ...sx,
      }}
    >
      {/* Em mobile: primário primeiro, secundário depois */}
      {/* Em desktop: secundário primeiro, primário depois */}
      {isMobile ? (
        <>
          {/* Botões primários com texto (fullWidth) */}
          {primaryText.map(action => renderButton(action, true))}
          
          {/* Botões com ícone apenas agrupados em linha */}
          {(primaryIconOnly.length > 0 || secondaryIconOnly.length > 0) && (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'center',
                width: '100%',
                py: 1,
              }}
            >
              {primaryIconOnly.map(action => renderButton(action, true))}
              {secondaryIconOnly.map(action => renderButton(action, false))}
            </Box>
          )}
          
          {/* Botões secundários com texto (fullWidth) */}
          {secondaryText.map(action => renderButton(action, false))}
        </>
      ) : (
        <>
          {secondaryActions.map(action => renderButton(action, false))}
          {primaryActions.map(action => renderButton(action, true))}
        </>
      )}
    </DialogActions>
  );
};

export default ResponsiveDialogActions;

